import { apiClient } from './client';
import { 
  Assessment, 
  AssessmentCreate, 
  AssessmentUpdate, 
  AssessmentFilters,
  AssessmentScale,
  AssessmentScaleCreate,
  AssessmentScaleUpdate,
  AssessmentSummary,
  LearningArea,
  LearningAreaCreate,
  LearningAreaUpdate,
  LearningOutcome,
  LearningOutcomeCreate,
  LearningOutcomeUpdate,
  LearningPeriod,
  LearningPeriodCreate,
  LearningPeriodUpdate,
  PaginatedResponse 
} from '../../types/api';

const BASE_URL = '/assessments';

export const assessmentService = {
  // Assessment CRUD
  async getAssessments(filters?: AssessmentFilters): Promise<PaginatedResponse<Assessment>> {
    const params = new URLSearchParams();
    
    if (filters?.child_id) params.append('child_id', filters.child_id);
    if (filters?.learning_area_id) params.append('learning_area_id', filters.learning_area_id);
    if (filters?.learning_outcome_id) params.append('learning_outcome_id', filters.learning_outcome_id);
    if (filters?.grade_id) params.append('grade_id', filters.grade_id);
    if (filters?.classroom_id) params.append('classroom_id', filters.classroom_id);
    if (filters?.is_published !== undefined) params.append('is_published', filters.is_published.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.skip) params.append('skip', filters.skip.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    
    const response = await apiClient.get(`${BASE_URL}?${params.toString()}`);
    const items = Array.isArray(response.data) ? response.data : response.data.items || [];
    return { items, total: items.length, page: 1, size: items.length, pages: 1 };
  },

  async getAssessment(id: string): Promise<Assessment> {
    const response = await apiClient.get(`${BASE_URL}/${id}`);
    return response.data;
  },

  async createAssessment(data: AssessmentCreate): Promise<Assessment> {
    const response = await apiClient.post(BASE_URL, data);
    return response.data;
  },

  async updateAssessment(id: string, data: AssessmentUpdate): Promise<Assessment> {
    const response = await apiClient.put(`${BASE_URL}/${id}`, data);
    return response.data;
  },

  async deleteAssessment(id: string): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  // Assessment publishing
  async publishAssessment(id: string): Promise<void> {
    await apiClient.post(`${BASE_URL}/${id}/publish`);
  },

  async unpublishAssessment(id: string): Promise<void> {
    await apiClient.post(`${BASE_URL}/${id}/unpublish`);
  },

  // Assessment summary
  async getAssessmentSummary(childId: string): Promise<AssessmentSummary> {
    const response = await apiClient.get(`${BASE_URL}/summary/child/${childId}`);
    return response.data;
  },
};

export const assessmentScaleService = {
  // Assessment Scale CRUD
  async getAssessmentScales(): Promise<AssessmentScale[]> {
    const response = await apiClient.get(`${BASE_URL}/scales`);
    return response.data;
  },

  async getAssessmentScale(id: string): Promise<AssessmentScale> {
    const response = await apiClient.get(`${BASE_URL}/scales/${id}`);
    return response.data;
  },

  async createAssessmentScale(data: AssessmentScaleCreate): Promise<AssessmentScale> {
    const response = await apiClient.post(`${BASE_URL}/scales`, data);
    return response.data;
  },

  async updateAssessmentScale(id: string, data: AssessmentScaleUpdate): Promise<AssessmentScale> {
    const response = await apiClient.put(`${BASE_URL}/scales/${id}`, data);
    return response.data;
  },

  async deleteAssessmentScale(id: string): Promise<void> {
    await apiClient.delete(`${BASE_URL}/scales/${id}`);
  },
};

// Learning Area Service
export const learningAreaService = {
  async getLearningAreas(): Promise<LearningArea[]> {
    const response = await apiClient.get('/curriculum/areas');
    return response.data;
  },

  async getLearningArea(id: string): Promise<LearningArea> {
    const response = await apiClient.get(`/curriculum/areas/${id}`);
    return response.data;
  },

  async createLearningArea(data: LearningAreaCreate): Promise<LearningArea> {
    const response = await apiClient.post('/curriculum/areas', data);
    return response.data;
  },

  async updateLearningArea(id: string, data: LearningAreaUpdate): Promise<LearningArea> {
    const response = await apiClient.put(`/curriculum/areas/${id}`, data);
    return response.data;
  },

  async deleteLearningArea(id: string): Promise<void> {
    await apiClient.delete(`/curriculum/areas/${id}`);
  },
};

// Learning Outcome Service  
export const learningOutcomeService = {
  async getLearningOutcomes(learningAreaId?: string): Promise<LearningOutcome[]> {
    const params = new URLSearchParams();
    params.append('limit', '1000');
    if (learningAreaId) params.append('learning_area_id', learningAreaId);
    
    const response = await apiClient.get(`/curriculum/outcomes?${params.toString()}`);
    return response.data;
  },

  async getLearningOutcome(id: string): Promise<LearningOutcome> {
    const response = await apiClient.get(`/curriculum/outcomes/${id}`);
    return response.data;
  },

  async createLearningOutcome(data: LearningOutcomeCreate): Promise<LearningOutcome> {
    const response = await apiClient.post('/curriculum/outcomes', data);
    return response.data;
  },

  async updateLearningOutcome(id: string, data: LearningOutcomeUpdate): Promise<LearningOutcome> {
    const response = await apiClient.put(`/curriculum/outcomes/${id}`, data);
    return response.data;
  },

  async deleteLearningOutcome(id: string): Promise<void> {
    await apiClient.delete(`/curriculum/outcomes/${id}`);
  },
};

// Learning Period Service
export const learningPeriodService = {
  async getLearningPeriods(): Promise<LearningPeriod[]> {
    const response = await apiClient.get('/periods');
    return response.data;
  },

  async getLearningPeriod(id: string): Promise<LearningPeriod> {
    const response = await apiClient.get(`/periods/${id}`);
    return response.data;
  },

  async createLearningPeriod(data: LearningPeriodCreate): Promise<LearningPeriod> {
    const response = await apiClient.post('/periods', data);
    return response.data;
  },

  async updateLearningPeriod(id: string, data: LearningPeriodUpdate): Promise<LearningPeriod> {
    const response = await apiClient.put(`/periods/${id}`, data);
    return response.data;
  },

  async deleteLearningPeriod(id: string): Promise<void> {
    await apiClient.delete(`/periods/${id}`);
  },
};

// Export the main assessment API with all services
export const assessmentsApi = {
  ...assessmentService,
  getAssessmentScales: assessmentScaleService.getAssessmentScales,
  getAssessmentScale: assessmentScaleService.getAssessmentScale,
  createAssessmentScale: assessmentScaleService.createAssessmentScale,
  updateAssessmentScale: assessmentScaleService.updateAssessmentScale,
  deleteAssessmentScale: assessmentScaleService.deleteAssessmentScale,
  getLearningAreas: learningAreaService.getLearningAreas,
  getLearningArea: learningAreaService.getLearningArea,
  createLearningArea: learningAreaService.createLearningArea,
  updateLearningArea: learningAreaService.updateLearningArea,
  deleteLearningArea: learningAreaService.deleteLearningArea,
  getLearningOutcomes: learningOutcomeService.getLearningOutcomes,
  getLearningOutcome: learningOutcomeService.getLearningOutcome,
  createLearningOutcome: learningOutcomeService.createLearningOutcome,
  updateLearningOutcome: learningOutcomeService.updateLearningOutcome,
  deleteLearningOutcome: learningOutcomeService.deleteLearningOutcome,
  getLearningPeriods: learningPeriodService.getLearningPeriods,
  getLearningPeriod: learningPeriodService.getLearningPeriod,
  createLearningPeriod: learningPeriodService.createLearningPeriod,
  updateLearningPeriod: learningPeriodService.updateLearningPeriod,
  deleteLearningPeriod: learningPeriodService.deleteLearningPeriod,
};
